import { formatDate } from '../../../utils/dateFormat';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serviceAgentService, ServiceAgentInvestorDto } from '../../../services/serviceAgent.service';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { getPermissionErrorMessage } from '../../../utils/apiClient';
import './ServiceAgentDashboard.scss';

export const ServiceAgentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [investors, setInvestors] = useState<ServiceAgentInvestorDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    fetchInvestors();
  }, []);

  const fetchInvestors = async () => {
    try {
      const data = await serviceAgentService.getMyInvestors();
      setInvestors(data);
    } catch (err: any) {
      const permissionError = getPermissionErrorMessage(err);
      if (permissionError) {
        toast.error(permissionError);
      } else {
        toast.error('Failed to load investors');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = investors.filter(inv =>
    inv.investorName.toLowerCase().includes(search.toLowerCase()) ||
    inv.investorEmail.toLowerCase().includes(search.toLowerCase()) ||
    (inv.investorUniqueCode || '').toLowerCase().includes(search.toLowerCase()) ||
    (inv.serviceProviderName || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount  = investors.filter(i => i.delegationActive).length;
  const fullCount    = investors.filter(i => i.delegationScope === 'FULL_ONBOARDING').length;

  if (loading) return <LoadingSpinner />;

  const stats = [
    { label: 'Total Investors', value: investors.length },
    { label: 'Active Delegations', value: activeCount },
    { label: 'Full Access', value: fullCount },
  ];

  // Keep + digits only, so the tel: link dials correctly.
  const digits = (v?: string) => (v || '').replace(/[^\d+]/g, '');

  return (
    <div className="sa-dashboard facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        {/* Welcome banner */}
        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-3 px-4 py-3 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">Client Executive Agent Dashboard</h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">Manage investors assigned to you via active delegations.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/service-agent/introduce-investor')} className="bg-white text-[#2c5e6a] border border-white rounded px-2.5 py-1 text-[11px] font-semibold cursor-pointer hover:bg-white/90 transition-colors"><i className="bi bi-person-plus me-1"></i>Introduce Investor</button>
            <button onClick={() => navigate('/service-agent/investors')} className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors">All Investors</button>
            <button onClick={() => navigate('/service-agent/profile')} className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors">My Profile</button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          {stats.map((s, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-4 py-3">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{s.label}</div>
              <div className="text-[24px] font-bold text-slate-800 leading-tight">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Investor table card */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] flex flex-col">
          <div className="p-2 flex justify-between items-center border-b border-[#e2e8f0] flex-wrap gap-2">
            <h2 className="text-[14px] font-bold text-slate-800 flex items-center tracking-tight mb-0">
              <i className="bi bi-people mr-2 text-slate-500"></i> My Investors
            </h2>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 260 }}
              placeholder="Search by name, email, code..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {filtered.length === 0 ? (
            <div className="py-5 text-center text-[12px] text-slate-400">
              {investors.length === 0
                ? 'No investors have delegated access to you yet.'
                : 'No investors match your search.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr className="text-[10px] text-slate-500 text-uppercase">
                    <th>Investor</th>
                    <th>Service Provider</th>
                    <th>Mobile</th>
                    <th>Permissions</th>
                    <th>Valid Until</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.investorId}>
                      <td>
                        <div className="fw-semibold text-slate-800">{inv.investorName}</div>
                        <small className="text-muted">{inv.investorEmail}</small>
                      </td>
                      <td>
                        {/* Client scenario 2 (2026-10-27): the SP name on the first screen. */}
                        {inv.serviceProviderName
                          ? <span className="text-[12px] text-slate-700">{inv.serviceProviderName}</span>
                          : <small className="text-muted">—</small>}
                      </td>
                      <td>
                        {inv.investorMobile
                          ? <a href={`tel:${digits(inv.investorMobile)}`} className="text-[12px] text-[#2c5e6a] hover:underline">{inv.investorMobile}</a>
                          : <small className="text-muted">—</small>}
                      </td>
                      <td>
                        <div className="permission-icons">
                          {inv.canViewProfile     && <span title="View Profile">👁 View</span>}
                          {inv.canEditKyc         && <span title="Edit KYC">✏️ Edit</span>}
                          {inv.canUploadDocuments && <span title="Upload Docs">📎 Upload</span>}
                          {inv.canSubmitForms     && <span title="Submit Forms">✅ Submit</span>}
                        </div>
                      </td>
                      <td>
                        <small className={inv.validTo && new Date(inv.validTo) < new Date() ? 'text-danger' : 'text-muted'}>
                          {inv.validTo ? formatDate(inv.validTo) : 'No expiry'}
                        </small>
                      </td>
                      <td>
                        {inv.delegationActive
                          ? <span className="badge bg-success">Active</span>
                          : <span className="badge bg-secondary">Inactive</span>}
                        {/* Journey status (client 2026-10-25): a completed account opening used to look "open". */}
                        {inv.journeyStatus && (
                          <span
                            className={`badge ms-1 ${inv.journeyStatus === 'COMPLETED' ? 'bg-primary' : inv.journeyStatus === 'ABANDONED' ? 'bg-dark' : 'bg-warning text-dark'}`}
                            title="Account-opening journey"
                          >
                            {inv.journeyStatus === 'COMPLETED' ? 'Completed' : inv.journeyStatus === 'ABANDONED' ? 'Discontinued' : 'In progress'}
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/service-agent/investors/${inv.investorId}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default ServiceAgentDashboard;
